/**
 * Adapter for converting CustomTemplateBox (DTO) to RenderBox format.
 * This allows the designs editor to use the shared canvas-engine renderers.
 */

import {
  RenderBox,
  RenderImageBox,
  RenderTextBox,
  RenderGroupBox,
  RenderObjectBox,
  RenderSmartObjectBox,
  RenderVectorBox,
  RenderAdjustmentBox,
  RenderRect,
  RenderMaskRect,
  RenderBoxEffects,
  TextAnchor,
  TransformMatrix,
  TextRun,
} from './render-box';
import { resolveVerticalAlign } from './vertical-align';
import {
  isBoxLocked,
  isBoxTextWrap,
  TextTypeEnum,
  type BoxTextWrap,
  type TextParagraphRun,
} from '@lite-workspace/dto-fotoclass-templates';
import {
  photoPlacementRect,
  transformEffectOrigins,
  transformBoxGradientEffects,
  transformLayerStyleMasks,
  scaleMaskRect,
} from '@lite-workspace/photobook-model';
import type {
  AdjustmentBoxParams,
  AdjustmentBinaryResource,
  BezierPath,
  ObjectAssetVersions,
  SmartObjectFilter,
  TemplateSmartObjectSource,
  VectorBoxParams,
  VectorFillData,
  CompoundClipBakeRecipe,
  CustomTemplateBoxBase,
  InddFirstBaselineRule,
  LayerStyleMasks,
  PsdBlendIf,
  PsdGroupComposite,
  TextAreaShapeParams,
  TextPathParams,
  TextStyleRun,
  TextWarpParams,
} from '@lite-workspace/dto-fotoclass-templates';

/**
 * Base interface for CustomTemplateBox-like objects
 */
export interface TemplateBoxLike {
  effectReferencePoint?: { x: number; y: number };
  effectDocumentOrigin?: { x: number; y: number };
  type: string;
  id: string;
  rect: [number, number, number, number]; // [x, y, width, height]
  /** Decor trim window, same tuple shape as `rect`. Absent → renders untrimmed. */
  contentRect?: [number, number, number, number];
  rotation?: number;
  visible?: boolean;
  spreadWide?: boolean;
  textWrap?: BoxTextWrap;
  wornFromBackground?: boolean;
  locked?: boolean;
  opacity?: number;
  fillOpacity?: number;
  blendInteriorElements?: boolean;
  blendIf?: PsdBlendIf;
  knockout?: CustomTemplateBoxBase['knockout'];
  knockoutBackdrop?: CustomTemplateBoxBase['knockoutBackdrop'];
  blendClipped?: CustomTemplateBoxBase['blendClipped'];
  transparencyShapes?: CustomTemplateBoxBase['transparencyShapes'];
  layerStyleRendering?: 'live';
  layerStyleMasks?: LayerStyleMasks;
  blendMode?: string;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  effects?: RenderBoxEffects;
}

/**
 * Interface for CustomImageTemplateBox-like objects
 */
export interface ImageTemplateBoxLike extends TemplateBoxLike {
  type: 'image';
  params: {
    imageUrl?: string | null;
    previewUrl?: string | null;
    sourceRef?: string;
    maskDataUrl?: string;
    maskRect?: RenderMaskRect;
    maskUrl?: string;
    /** Id of the layer this frame is clipped to (Photoshop's clipping mask). */
    maskBaseBoxId?: string;
    frameUrl?: string;
    frameWindow?: { x: number; y: number; width: number; height: number };
    previewPixelData?: string;
    previewThumbHash?: string;
    paths?: readonly BezierPath[];
    versions?: {
      thumb?: { path: string; width: number; height: number };
      medium?: { path: string; width: number; height: number };
      thumbHash?: string;
    };
    assignedPhoto?: {
      photoId: string;
      url: string;
      nativeWidth: number;
      nativeHeight: number;
      /**
       * Пишет и читает только persist-нормализатор фотокниги (`@lite-workspace/photobook-model`):
       * адреса нарезанных версий и thumbHash, которым в `versions` не хватает width/height/fileId.
       * Путь авторинга шаблонов (`spread-to-template.ts`) этих полей не создаёт.
       */
      meta?: {
        thumbHash?: string;
        filename?: string;
        renditions?: { thumb?: string; small?: string; medium_1280?: string; big_1920?: string };
      };
    } | null;
    photoPlacementRect?: { left: number; top: number; width: number; height: number; angle?: number };
    fit?: 'contain';
    flipHorizontal?: boolean;
    flipVertical?: boolean;
    frameStrokeBaked?: boolean;
    cornerRadius?: number;
  };
}

/**
 * Interface for CustomTextTemplateBox-like objects
 */
export interface TextTemplateBoxLike extends TemplateBoxLike {
  type: 'text';
  params: {
    text?: string; // text content
    fontName: string;
    postScriptName?: string;
    fontWeight: string;
    fontSize: number;
    lineHeightPx?: number;
    color: string;
    /** PSD Gradient Overlay текстового слоя: при рендере перекрывает color. */
    fillGradient?: VectorFillData;
    align?: 'left' | 'center' | 'right' | 'justify';
    textMode?: 'block' | 'free';
    autoWidth?: boolean;
    autoFit?: boolean;
    maxFontSize?: number;
    verticalAlign?: 'top' | 'center' | 'bottom';
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    /** Photoshop faux bold — stroke-симуляция жирного начертания. Независим от fontWeight. */
    fauxBold?: boolean;
    textType?: string;
    /** Рамка цитаты серии виньетки (R409) и её пометки: своя ёмкость рамки — `quoteFloorCapacity`. */
    vignetteQuote?: boolean;
    leadLine?: boolean;
    quoteFloorCapacity?: number;
    tracking?: number;
    leading?: number;
    fontCaps?: number;
    fontBaseline?: number;
    horizontalScale?: number;
    verticalScale?: number;
    orientation?: 'vertical';
    superscriptSize?: number;
    superscriptPosition?: number;
    subscriptSize?: number;
    subscriptPosition?: number;
    smallCapSize?: number;
    useFractionalGlyphWidths?: boolean;
    frameOnPenBox?: boolean;
    antiAlias?: number;
    firstBaselineOffset?: number;
    firstBaselineRule?: 'ascent' | 'ink' | InddFirstBaselineRule;
    firstBaselineMin?: number;
    textLayout?: number;
    firstLineIndent?: number;
    startIndent?: number;
    endIndent?: number;
    spaceBefore?: number;
    spaceAfter?: number;
    autoHyphenate?: boolean;
    hyphenatedWordSize?: number;
    preHyphen?: number;
    postHyphen?: number;
    consecutiveHyphens?: number;
    hyphenationZone?: number;
    language?: number;
    leadingType?: number;
    everyLineComposer?: boolean;
    hanging?: boolean;
    paragraphRuns?: readonly TextParagraphRun[];
    autoKerning?: boolean;
    kerning?: number;
    ligatures?: boolean;
    dLigatures?: boolean;
    noBreak?: boolean;
    strokeColor?: string;
    fillFlag?: boolean;
    strokeFlag?: boolean;
    fillFirst?: boolean;
    outlineWidth?: number;
    yUnderline?: number;
    transform?: TransformMatrix;
    anchor?: TextAnchor;
    textPath?: TextPathParams;
    areaShape?: TextAreaShapeParams;
    ignoreTextWrap?: boolean;
    textWrapGroups?: string[];
    warp?: TextWarpParams;
    styleRuns?: TextStyleRun[];
  };
}

/**
 * Interface for CustomGroupTemplateBox-like objects
 */
export interface GroupTemplateBoxLike extends TemplateBoxLike {
  type: 'group';
  boxes: TemplateBoxLike[];
  /** `false` pins the frame in place (the enlarged vignette slot). */
  draggable?: boolean;
  params?: {
    personId?: string | null;
    peopleType?: string;
    psdGroup?: PsdGroupComposite;
  };
}

/**
 * Interface for CustomObjectTemplateBox-like objects
 */
export interface ObjectTemplateBoxLike extends TemplateBoxLike {
  type: 'object' | 'background';
  params: {
    pixelData?: string; // base64 string
    url?: string; // URL of uploaded image
    fileId?: string;
    versions?: {
      thumb?: { path: string; width: number; height: number };
      medium?: { path: string; width: number; height: number };
      /** 1920 screen tier of a flat background composite; absent on every other object asset. */
      large?: { path: string; width: number; height: number };
      thumbHash?: string;
    };
    maskDataUrl?: string;
    maskRect?: RenderMaskRect;
    /** Слой, которым этот декор обрезан (обтравочная маска) — у фото-слота клип-группы до назначения рамкой. */
    maskBaseBoxId?: string;
    maskUrl?: string;
    compoundClipBakeRecipe?: CompoundClipBakeRecipe;
  };
}

/**
 * Interface for SmartObject template box-like objects
 */
export interface SmartObjectTemplateBoxLike extends TemplateBoxLike {
  type: 'smartObject';
  params: {
    pixelData?: string;
    url?: string;
    sourceType?: string;
    sourceRef?: string;
    transform?: TransformMatrix;
    versions?: {
      thumb?: { path: string; width: number; height: number };
      medium?: { path: string; width: number; height: number };
      thumbHash?: string;
    };
    maskDataUrl?: string;
    maskUrl?: string;
    smartFilters?: readonly SmartObjectFilter[];
  };
}

/**
 * Interface for Vector template box-like objects.
 * Mirrors CustomVectorTemplateBox from dto-fotoclass-templates.
 */
export interface VectorTemplateBoxLike extends TemplateBoxLike {
  type: 'vector';
  params: VectorBoxParams;
}

/**
 * Interface for Adjustment template box-like objects. Mirrors CustomAdjustmentTemplateBox.
 */
export interface AdjustmentTemplateBoxLike extends TemplateBoxLike {
  type: 'adjustment';
  params: AdjustmentBoxParams;
}

/**
 * Union type for all template box-like objects
 */
export type TemplateBoxUnion =
  | ImageTemplateBoxLike
  | TextTemplateBoxLike
  | GroupTemplateBoxLike
  | ObjectTemplateBoxLike
  | SmartObjectTemplateBoxLike
  | VectorTemplateBoxLike
  | AdjustmentTemplateBoxLike;

export interface TemplateAdapterContext {
  adjustmentResources?: readonly AdjustmentBinaryResource[];
  smartObjectSources?: ReadonlyArray<
    Pick<TemplateSmartObjectSource, 'id' | 'url' | 'naturalWidth' | 'naturalHeight' | 'versions'>
  >;
  useRuntimeSO?: boolean;
}

/** Кегль редактора по умолчанию (`DEFAULT_TEXT_PARAMS.fontSize`) — подставляется боксу без своего размера. */
const FALLBACK_FONT_SIZE = 16;

/**
 * Своя ёмкость рамки цитаты серии (R10): набор режется по ЧИСЛУ знаков, а не по краю прижатой рамки. Строка
 * руководителя (`leadLine`) — нет: в неё дописывается подпись. Ключ только при значении.
 */
function quoteCapacityOf(params: TextTemplateBoxLike['params']): { quoteCapacity?: number } {
  const { vignetteQuote, leadLine, quoteFloorCapacity } = params;
  return vignetteQuote === true && leadLine !== true && quoteFloorCapacity !== undefined && quoteFloorCapacity > 0
    ? { quoteCapacity: quoteFloorCapacity }
    : {};
}

/**
 * Adapter for converting CustomTemplateBox* models to RenderBox format.
 */
export class TemplateBoxAdapter {
  /**
   * Convert any template box to a RenderBox
   * @param box - The template box to convert
   */
  static toRenderBox(box: TemplateBoxUnion, context?: TemplateAdapterContext): RenderBox {
    const [x, y, width, height] = box.rect;
    const baseRect: RenderRect = {
      x,
      y,
      width,
      height,
      rotation: box.rotation,
    };

    return this.toRender(box as TemplateBoxLike, baseRect, context);
  }

  static toRender(box: TemplateBoxLike, baseRect: RenderRect, context?: TemplateAdapterContext): RenderBox {
    let rendered = this.dispatchRender(box, baseRect, context);
    rendered = { ...rendered, ...transformEffectOrigins(box, [1, 0, 0, 1, baseRect.x - box.rect[0], baseRect.y - box.rect[1]]) };
    if (rendered.maskRect)
      rendered = {
        ...rendered,
        maskRect: scaleMaskRect(rendered.maskRect, 1, 1, baseRect.x - box.rect[0], baseRect.y - box.rect[1]),
      };
    if (box.layerStyleRendering === 'live') {
      const placement = [1, 0, 0, 1, baseRect.x - box.rect[0], baseRect.y - box.rect[1]] as const;
      rendered = {
        ...rendered,
        layerStyleRendering: 'live',
        layerStyleMasks: transformLayerStyleMasks(box.layerStyleMasks, placement),
        fillOpacity: box.fillOpacity,
        blendInteriorElements: box.blendInteriorElements,
        ...(rendered.type !== 'text' &&
          rendered.effects && {
            effects: transformBoxGradientEffects(rendered.effects, placement),
          }),
      };
    }

    if (box.blendIf && rendered.type !== 'group') rendered = { ...rendered, blendIf: box.blendIf };
    if (box.knockout && rendered.type !== 'group')
      rendered = { ...rendered, knockout: box.knockout, ...(box.knockoutBackdrop && { knockoutBackdrop: box.knockoutBackdrop }) };
    if (box.blendClipped === false && rendered.type !== 'group') rendered = { ...rendered, blendClipped: false };
    if (box.transparencyShapes === false && rendered.type !== 'group') rendered = { ...rendered, transparencyShapes: false };

    if (rendered.type === 'text' && rendered.effects) {
      rendered = {
        ...rendered,
        effects: transformBoxGradientEffects(rendered.effects, [1, 0, 0, 1, baseRect.x - box.rect[0], baseRect.y - box.rect[1]]),
      };
    }

    // Undefined stays absent (not `visible: undefined`) — the persisted JSON shape has no key either,
    // and the album path (DrawableAdapter) emits the flag under the same condition.
    if (box.visible !== undefined) {
      rendered = { ...rendered, visible: box.visible };
    }

    if (box.spreadWide === true) rendered = { ...rendered, spreadWide: true };
    // A layer worn from a background tile lies under the spread's text: it wraps nothing, like a box typed to background.
    if (isBoxTextWrap(box.textWrap) && box.wornFromBackground !== true) rendered = { ...rendered, textWrap: box.textWrap };

    return isBoxLocked(box) ? lockRenderSubtree(rendered) : rendered;
  }

  private static dispatchRender(box: TemplateBoxLike, baseRect: RenderRect, context?: TemplateAdapterContext): RenderBox {
    switch (box.type) {
      case 'image':
        return this.toRenderImageBox(box as ImageTemplateBoxLike, baseRect, context);
      case 'text':
        return this.toRenderTextBox(box as TextTemplateBoxLike, baseRect);
      case 'group':
        return this.toRenderGroupBox(box as GroupTemplateBoxLike, baseRect, context);
      case 'object':
      case 'background':
        return this.toRenderObjectBox(box as ObjectTemplateBoxLike, baseRect);
      case 'smartObject':
        return this.toRenderSmartObjectBox(box as SmartObjectTemplateBoxLike, baseRect, context);
      case 'vector':
        return this.toRenderVectorBox(box as VectorTemplateBoxLike, baseRect);
      case 'adjustment':
        return this.toRenderAdjustmentBox(box as AdjustmentTemplateBoxLike, baseRect, context);
      default:
        throw new Error(`Unknown template box type: ${(box as TemplateBoxLike).type}`);
    }
  }

  private static toRenderVectorBox(box: VectorTemplateBoxLike, rect: RenderRect): RenderVectorBox {
    return {
      type: 'vector',
      id: box.id,
      rect,
      opacity: box.opacity,
      blendMode: box.blendMode,
      flipHorizontal: box.flipHorizontal,
      flipVertical: box.flipVertical,
      vector: box.params,
      // Legacy shapes baked their effects into tiles; only live ones hand them to the renderer.
      ...(box.layerStyleRendering === 'live' && box.effects && { effects: box.effects }),
    };
  }

  private static toRenderAdjustmentBox(
    box: AdjustmentTemplateBoxLike,
    rect: RenderRect,
    context?: TemplateAdapterContext,
  ): RenderAdjustmentBox {
    return {
      type: 'adjustment',
      id: box.id,
      rect,
      opacity: box.opacity,
      blendMode: box.blendMode,
      adjustment: box.params,
      adjustmentResources: context?.adjustmentResources,
    };
  }

  private static mapEffects(box: TemplateBoxLike): RenderBoxEffects | undefined {
    return box.effects;
  }

  private static toRenderImageBox(box: ImageTemplateBoxLike, rect: RenderRect, context?: TemplateAdapterContext): RenderImageBox {
    // A sample the browser cannot decode (PSB) drew nothing at all — not even the empty-frame placeholder.
    const matchedSource = box.params.sourceRef
      ? context?.smartObjectSources?.find(s => s.id === box.params.sourceRef && s.naturalWidth > 0 && s.naturalHeight > 0)
      : undefined;

    const v = box.params.versions;
    const versionUrl = v?.medium?.path ?? v?.thumb?.path;
    const assigned = box.params.assignedPhoto;

    // Каждое поле называет свою версию: адрес назначенного фото — это ОРИГИНАЛ, и лежит он в `image`;
    // нарезки едут своими именами из `meta.renditions` (в `versions` им не хватает width/height/fileId).
    // Пробрасывать их обязательно: печатная лестница (`box_asset_url`) читает big_1920/medium_1280/small/thumb
    // и `image` не знает — без нарезок и без `imageUrl` ниже фотобокс приезжал в печать без источника
    // и вырождался в серый плейсхолдер пустой рамки.
    const renditions = assigned?.meta?.renditions;
    const imageAlbum = assigned
      ? {
          id: assigned.photoId,
          image: assigned.url,
          big_1920: renditions?.big_1920,
          medium_1280: renditions?.medium_1280,
          small: renditions?.small,
          thumb: renditions?.thumb,
          thumb_hash: assigned.meta?.thumbHash,
          // Retoucher-facing photo identity — the album path shows it, so the template path must too.
          filename: assigned.meta?.filename,
          width: assigned.nativeWidth,
          height: assigned.nativeHeight,
        }
      : box.params.imageUrl
        ? { thumb: box.params.previewUrl || undefined, image: box.params.imageUrl }
        : undefined;

    // Тот же закон кадра, что у декода (image-box.ts): печать и серверное превью читают модель через
    // decode, холст — напрямую по персисту, и окно, снятое с рамки, обязано лечь одинаково на обоих путях.
    const placement = assigned
      ? photoPlacementRect(box.params.photoPlacementRect, assigned.nativeWidth, assigned.nativeHeight, rect, box.params.fit)
      : undefined;
    const imageRect = placement
      ? { x: placement.left, y: placement.top, width: placement.width, height: placement.height, rotation: placement.angle }
      : undefined;

    return {
      type: 'image',
      id: box.id,
      rect,
      opacity: box.opacity,
      blendMode: box.blendMode,
      effects: this.mapEffects(box),
      // Оригинал названного фото — верхняя ступень источника, как и на legacy-проводе (`source_url()`:
      // image -> big_1920 -> medium_1280 -> small -> thumb). Ступени `image` у печатной лестницы нет,
      // а печать 300 dpi обязана брать исходник, а не 1920-й файл: полосу продолжают ровно те же
      // нарезки из `imageAlbum`, так что порядок источников совпадает с legacy рунг в рунг.
      // Браузерная лестница (`imageBoxUrl`) это поле не читает вовсе — превью остаётся на нарезках.
      imageUrl: assigned?.url || undefined,
      imageAlbum,
      imageRect,
      flipHorizontal: box.params.flipHorizontal,
      flipVertical: box.params.flipVertical,
      previewSourceUrl: matchedSource?.url ?? versionUrl,
      previewPixelData: box.params.previewPixelData,
      previewThumbHash: v?.thumbHash ?? box.params.previewThumbHash,
      maskDataUrl: box.params.maskDataUrl ?? box.params.maskUrl,
      maskRect: box.params.maskRect,
      maskBaseBoxId: box.params.maskBaseBoxId,
      frameUrl: box.params.frameUrl,
      frameWindow: box.params.frameWindow,
      paths: box.params.paths,
      // Вердикт «обводку уже запёк шаблон» ставит проекция шаблона на разворот и хранит сам бокс: из
      // рендер-модели он не выводится (шаблонного родства в ней нет). Один читатель на оба рендера —
      // редактор идёт сюда через `collectTemplateRenderBoxes`, печать через `print-wire`.
      frameStrokeBaked: box.params.frameStrokeBaked,
      cornerRadius: box.params.cornerRadius,
    };
  }

  private static toRenderTextBox(box: TextTemplateBoxLike, rect: RenderRect): RenderTextBox {
    // Кегль обязан быть конечным числом: он множит каждую глифовую координату, а `JSON.stringify`
    // отдаёт NaN как `null` — движок (Rust, `f32` без Option) на таком роняет ВЕСЬ разворот
    // («invalid type: null, expected f32»), а не один бокс. Текстовый бокс без размера приезжает
    // от авторинга по API (`params: {text}`), поэтому нормализуем на входе в рендер-модель.
    const fontSize = Number.isFinite(box.params.fontSize) ? box.params.fontSize : FALLBACK_FONT_SIZE;

    return {
      type: 'text',
      id: box.id,
      rect,
      fillOpacity: box.fillOpacity,
      blendInteriorElements: box.blendInteriorElements,
      opacity: box.opacity,
      blendMode: box.blendMode,
      effects: this.mapEffects(box),
      text: box.params.text || '',
      fontName: box.params.fontName,
      postScriptName: box.params.postScriptName,
      fontWeight: box.params.fontWeight,
      italic: box.params.italic ?? false,
      fontSize,
      color: box.params.color,
      fillGradient: box.params.fillGradient,
      textType: box.params.textType,
      ...quoteCapacityOf(box.params),
      align: box.params.align || 'left',
      // Цитата всегда блок-контейнер: рамки, сохранённые до этого правила, доопределяем при чтении.
      textMode: box.params.textType === TextTypeEnum.PERSON_QUOTE ? 'block' : (box.params.textMode ?? 'free'),
      autoWidth: box.params.autoWidth ?? false,
      autoFit: box.params.autoFit ?? false,
      maxFontSize: box.params.maxFontSize,
      verticalAlign: resolveVerticalAlign(box.params.verticalAlign),
      tracking: box.params.tracking,
      // || not ??: templates parsed before the FKS-11323 zero-leading fix persist lineHeightPx/leading 0
      // (auto-leading marker) — honoring the stored zero collapses every line onto one baseline.
      lineHeight: box.params.lineHeightPx || box.params.leading || fontSize * 1.2,
      underline: box.params.underline ?? false,
      strikethrough: box.params.strikethrough ?? false,
      fauxBold: box.params.fauxBold ?? false,
      fontCaps: box.params.fontCaps,
      fontBaseline: box.params.fontBaseline,
      horizontalScale: box.params.horizontalScale,
      verticalScale: box.params.verticalScale,
      orientation: box.params.orientation,
      superscriptSize: box.params.superscriptSize,
      superscriptPosition: box.params.superscriptPosition,
      subscriptSize: box.params.subscriptSize,
      subscriptPosition: box.params.subscriptPosition,
      smallCapSize: box.params.smallCapSize,
      useFractionalGlyphWidths: box.params.useFractionalGlyphWidths,
      frameOnPenBox: box.params.frameOnPenBox,
      antiAlias: box.params.antiAlias,
      firstBaselineOffset: box.params.firstBaselineOffset,
      firstBaselineRule: box.params.firstBaselineRule,
      firstBaselineMin: box.params.firstBaselineMin,
      textLayout: box.params.textLayout,
      firstLineIndent: box.params.firstLineIndent,
      startIndent: box.params.startIndent,
      endIndent: box.params.endIndent,
      spaceBefore: box.params.spaceBefore,
      spaceAfter: box.params.spaceAfter,
      autoHyphenate: box.params.autoHyphenate,
      hyphenatedWordSize: box.params.hyphenatedWordSize,
      preHyphen: box.params.preHyphen,
      postHyphen: box.params.postHyphen,
      consecutiveHyphens: box.params.consecutiveHyphens,
      hyphenationZone: box.params.hyphenationZone,
      language: box.params.language,
      leadingType: box.params.leadingType,
      everyLineComposer: box.params.everyLineComposer,
      hanging: box.params.hanging,
      paragraphRuns: box.params.paragraphRuns,
      autoKerning: box.params.autoKerning,
      kerning: box.params.kerning,
      ligatures: box.params.ligatures,
      dLigatures: box.params.dLigatures,
      noBreak: box.params.noBreak,
      strokeColor: box.params.strokeColor,
      fillFlag: box.params.fillFlag,
      strokeFlag: box.params.strokeFlag,
      fillFirst: box.params.fillFirst,
      outlineWidth: box.params.outlineWidth,
      yUnderline: box.params.yUnderline,
      transform: box.params.transform,
      anchor: box.params.anchor,
      textPath: box.params.textPath,
      areaShape: box.params.areaShape,
      ...(box.params.ignoreTextWrap && { ignoreTextWrap: true }),
      ...(box.params.textWrapGroups && box.params.textWrapGroups.length > 0 && { textWrapGroups: box.params.textWrapGroups }),
      warp: box.params.warp,
      runs: box.params.styleRuns?.map(
        (r): TextRun => ({
          from: r.from,
          to: r.to,
          fontName: r.fontName,
          fontSize: r.fontSize,
          lineHeightPx: r.lineHeightPx,
          color: r.color,
          tracking: r.tracking,
          fauxBold: r.fauxBold,
          italic: r.italic,
          underline: r.underline,
          strikethrough: r.strikethrough,
          fontCaps: r.fontCaps,
          fontBaseline: r.fontBaseline,
          horizontalScale: r.horizontalScale,
          verticalScale: r.verticalScale,
          baselineShift: r.baselineShift,
          autoKerning: r.autoKerning,
          kerning: r.kerning,
          ligatures: r.ligatures,
          dLigatures: r.dLigatures,
          fontWeight: r.fontWeight,
          language: r.language,
          styleRunAlignment: r.styleRunAlignment,
          noBreak: r.noBreak,
          strokeColor: r.strokeColor,
          fillFlag: r.fillFlag,
          strokeFlag: r.strokeFlag,
          fillFirst: r.fillFirst,
          outlineWidth: r.outlineWidth,
          yUnderline: r.yUnderline,
        }),
      ),
    };
  }

  private static toRenderGroupBox(box: GroupTemplateBoxLike, rect: RenderRect, context?: TemplateAdapterContext): RenderGroupBox {
    return {
      type: 'group',
      id: box.id,
      rect,
      opacity: box.opacity,
      blendMode: box.blendMode,
      personId: box.params?.personId,
      peopleType: box.params?.peopleType,
      draggable: box.draggable,
      ...(box.params?.psdGroup && { psdGroup: box.params.psdGroup }),
      // Group styles composite the group as one layer (ADR 0043); a plain group has none to hand over.
      ...(box.layerStyleRendering === 'live' && box.effects && { effects: box.effects }),
      // Hidden children ride through with visible:false (the engine skips the subtree itself) —
      // dropping them here would diverge from the album path and lose the layer for hit/panel consumers.
      children: (box.boxes ?? []).map(child => this.toRenderBox(child as TemplateBoxUnion, context)),
    };
  }

  private static toContentRect(box: TemplateBoxLike): RenderRect | undefined {
    if (!box.contentRect) return undefined;
    const [x, y, width, height] = box.contentRect;
    return { x, y, width, height };
  }

  private static toRenderObjectBox(box: ObjectTemplateBoxLike, rect: RenderRect): RenderObjectBox {
    return {
      type: box.type as 'object' | 'background',
      id: box.id,
      rect,
      contentRect: this.toContentRect(box),
      opacity: box.opacity,
      blendMode: box.blendMode,
      flipHorizontal: box.flipHorizontal,
      flipVertical: box.flipVertical,
      effects: this.mapEffects(box),
      url: box.params.url || undefined,
      pixelData: box.params.pixelData || undefined,
      versions: box.params.versions
        ? {
            thumb: box.params.versions.thumb?.path,
            medium: box.params.versions.medium?.path,
            large: box.params.versions.large?.path,
            thumbHash: box.params.versions.thumbHash,
          }
        : undefined,
      maskDataUrl: box.params.maskDataUrl ?? box.params.maskUrl,
      maskRect: box.params.maskRect,
      maskBaseBoxId: box.params.maskBaseBoxId,
      compoundClipBakeRecipe: box.params.compoundClipBakeRecipe,
    };
  }

  private static toRenderSmartObjectBox(
    box: SmartObjectTemplateBoxLike,
    rect: RenderRect,
    context?: TemplateAdapterContext,
  ): RenderSmartObjectBox {
    const { params } = box;
    // Forced runtime path for clipping-photo boxes (carry an alpha mask): the embedded
    // JPEG behind the mask must be rendered live; legacy pixelData would only be the mask.
    const effectiveRuntimeSO = context?.useRuntimeSO === true || !!params.maskDataUrl || !!params.maskUrl;
    // The baked pixels hold Photoshop's own filter result; a filter the engine cannot run keeps them instead of the
    // raw source. A clipping photo stays on the source: its legacy pixels may be the mask alone.
    const filters = params.smartFilters ?? [];
    const allLive = filters.every(isLiveSmartFilter);
    const sourceAllowed = allLive || !!params.maskDataUrl || !!params.maskUrl;
    // naturalWidth/Height = 0 → source mime не загружается в <img> (например PSB).
    // Тогда не выставляем sourceUrl, чтобы renderer ушёл на pixelData/url fallback.
    const matchedSource =
      effectiveRuntimeSO && sourceAllowed && params.sourceRef
        ? context?.smartObjectSources?.find(s => s.id === params.sourceRef && s.naturalWidth > 0 && s.naturalHeight > 0)
        : undefined;

    return {
      type: 'smartObject',
      id: box.id,
      rect,
      contentRect: this.toContentRect(box),
      opacity: box.opacity,
      blendMode: box.blendMode,
      // The rasterizer mirrors by it on both paths: inside the pixel footprint, or the placed source through the rect centre.
      ...(box.flipHorizontal === true && { flipHorizontal: true }),
      ...(box.flipVertical === true && { flipVertical: true }),
      effects: this.mapEffects(box),
      pixelData: params.pixelData || undefined,
      url: params.url || undefined,
      sourceType: params.sourceType,
      thumbHash: params.versions?.thumbHash,
      versions: params.versions
        ? {
            thumb: params.versions.thumb?.path,
            medium: params.versions.medium?.path,
          }
        : undefined,
      sourceUrl: matchedSource?.url,
      sourceVersions: toSourceTiers(matchedSource?.versions),
      sourceTransform: params.transform,
      sourceNaturalWidth: matchedSource?.naturalWidth,
      sourceNaturalHeight: matchedSource?.naturalHeight,
      useRuntimeSO: effectiveRuntimeSO,
      maskDataUrl: params.maskDataUrl ?? params.maskUrl,
      ...(matchedSource && allLive && filters.length ? { smartFilters: filters.filter(isLiveSmartFilter) } : {}),
    };
  }
}

function isLiveSmartFilter(f: SmartObjectFilter): f is Exclude<SmartObjectFilter, { kind: 'unsupported' }> {
  return f.kind !== 'unsupported';
}

// The source shares `ObjectAssetVersions` with spread previews, whose provenance and ownership fields (source,
// previewGeneration, inheritedFrom, …) mean nothing for a smart-object source and would ride every render of the
// box. Only the two renditions a renderer can place cross into the render model.
function toSourceTiers(versions: ObjectAssetVersions | undefined): Pick<ObjectAssetVersions, 'thumb' | 'medium'> | undefined {
  if (!versions?.thumb && !versions?.medium) return undefined;

  return {
    ...(versions.thumb ? { thumb: versions.thumb } : {}),
    ...(versions.medium ? { medium: versions.medium } : {}),
  };
}

// A locked group holds its whole subtree: the engine skips drawables one at a time, so every
// descendant has to carry the flag itself.
function lockRenderSubtree(box: RenderBox): RenderBox {
  if (box.type === 'group') {
    return { ...box, locked: true, children: box.children.map(lockRenderSubtree) };
  }

  return { ...box, locked: true };
}

/**
 * Компайл-тайм паритет проекции: inline-сессия возвращает runs В МОДЕЛЬ, поэтому TextRun обязан нести
 * каждое поле TextStyleRun. Новое поле в DTO без зеркала здесь — ошибка компиляции, не тихая потеря.
 */
type MissingStyleRunFields = Exclude<keyof TextStyleRun, keyof TextRun>;
type AssertLossless<T extends never> = T;
export type _TextRunProjectionIsLossless = AssertLossless<MissingStyleRunFields>;
